import { useEffect, useMemo, useState } from 'react';
import type { Track } from '../lib/api';

type Props = {
  tracks: Track[];
};

export default function Player({ tracks }: Props) {
  const [current, setCurrent] = useState<Track | null>(tracks[0] ?? null);
  const [index, setIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [changing, setChanging] = useState(false);
  const audio = useMemo(() => new Audio(), []);

  useEffect(() => {
    if (!current) return undefined;
    setChanging(true);
    const timer = setTimeout(() => setChanging(false), 450);
    if (!current.mp3_file) {
      setIsPlaying(false);
      return () => clearTimeout(timer);
    }
    audio.src = current.mp3_file;
    audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    const onEnded = () => setIsPlaying(false);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.pause();
      audio.removeEventListener('ended', onEnded);
      clearTimeout(timer);
    };
  }, [audio, current]);

  useEffect(() => {
    if (!tracks.length) {
      setCurrent(null);
      setIndex(0);
      setIsPlaying(false);
      return;
    }
    const stillExists = current ? tracks.find((t) => t.id === current.id) : null;
    if (!stillExists) {
      setCurrent(tracks[0]);
      setIndex(0);
      setIsPlaying(false);
    }
  }, [tracks, current]);

  const playTrack = (track: Track, idx: number) => {
    setCurrent(track);
    setIndex(idx);
  };

  const next = () => {
    if (!tracks.length) return;
    const nextIndex = (index + 1) % tracks.length;
    playTrack(tracks[nextIndex], nextIndex);
  };

  const prev = () => {
    if (!tracks.length) return;
    const prevIndex = (index - 1 + tracks.length) % tracks.length;
    playTrack(tracks[prevIndex], prevIndex);
  };

  const toggle = () => {
    if (!current || !current.mp3_file) return;
    if (audio.paused) {
      audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const stop = () => {
    audio.pause();
    audio.currentTime = 0;
    setIsPlaying(false);
  };

  return (
    <div className="card jukebox-shell">
      <div className="jukebox-arch" />
      <div className="jukebox-body">
        <div className="jukebox-disc-area">
          <div className="jukebox-window">
            <div
              className={`vinyl vinyl-lg ${isPlaying ? 'spin' : ''} ${changing ? 'vinyl-change' : ''}`}
              onClick={toggle}
              style={current?.cover_image ? { backgroundImage: `url(${current.cover_image})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
            >
            </div>
            <div className={`tonearm ${isPlaying ? 'tonearm-play' : ''}`} />
          </div>
          <div className="current-track-title">{current?.title ?? 'Nessun brano'}</div>
          <div className="jukebox-marquee">
            <span className="badge badge-amber">Now spinning</span>
            <strong>{current?.title ?? 'Nessun brano'}</strong>
            {current?.year && <span className="badge">{current.year}</span>}
          </div>
          <div className="jukebox-controls">
            <button className="btn btn-ghost" type="button" onClick={prev} disabled={!tracks.length}>Prev</button>
            <button className="btn btn-primary" type="button" onClick={toggle} disabled={!current?.mp3_file}>
              {isPlaying ? 'Pausa' : 'Play'}
            </button>
            <button className="btn btn-ghost" type="button" onClick={stop} disabled={!current?.mp3_file}>Stop</button>
            <button className="btn btn-ghost" type="button" onClick={next} disabled={!tracks.length}>Next</button>
          </div>
          {!current?.mp3_file && <span className="badge">Nessun audio disponibile</span>}
        </div>

        <div className="jukebox-carousel">
          <div className="carousel-track">
            {tracks.filter((t) => t.id !== current?.id).map((track) => {
              const trackIndex = tracks.findIndex((t) => t.id === track.id);
              return (
                <button
                  key={track.id}
                  className="disk-card"
                  type="button"
                  aria-label={track.title}
                  onClick={() => playTrack(track, trackIndex)}
                >
                  <div
                    className="disk-thumb"
                    style={track.cover_image ? { backgroundImage: `url(${track.cover_image})` } : undefined}
                  >
                    <span className="disk-hole" />
                  </div>
                  <span className="disk-title">{track.title}</span>
                </button>
              );
            })}
          </div>
          {!tracks.length && <p style={{ margin: '12px 0 0' }}>Nessun brano caricato.</p>}
        </div>
      </div>
    </div>
  );
}
